import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useLocalization } from "@fluent/react";
import { Button, CollapsibleCard, Row, Tooltip } from "@narmi/design_system";
import Filters from "byzantine/src/filters";
import { DisplayStatus } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates";
import { formatBulkTemplateVersion } from "byzantine/src/dbbl/businessLogic/entities/bulkTemplates/helpers";
import { useFetchDualApprovalById } from "byzantine/src/dbbl/businessLogic/entities/dualApprovals";
import { StatusTag, getStatusDotClass } from "../../../PaymentActivity/common";
import styles from "./Drawer.module.scss";

type VersionCardProps = {
  version: API.BulkTemplateVersion;
  groupState: string;
  activeVersionId: API.BulkTemplateVersionId | null;
  onViewPayments: (version: API.BulkTemplateVersion) => void;
  onCopyToNewVersion?: (versionId: API.BulkTemplateVersionId) => void;
};

type DetailField = {
  label: string;
  value: string;
  tooltipText?: string;
};

const VersionCard = ({
  version,
  groupState,
  activeVersionId,
  onViewPayments,
  onCopyToNewVersion,
}: VersionCardProps) => {
  const { l10n } = useLocalization();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [responderName, setResponderName] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState<string | null>(null);
  const { send: fetchDualApproval } = useFetchDualApprovalById();

  type BulkTemplateDisplayStatus =
    (typeof DisplayStatus)[keyof typeof DisplayStatus];

  // infer display status from dual_approval_state, active version, and group state
  // "archived" for non-active versions w. no dual approvals is a frontend status only
  const getDisplayStatus = (): BulkTemplateDisplayStatus => {
    if (groupState === "archived") {
      return DisplayStatus.archived;
    }
    if (version.dual_approval_state === "awaiting_approval") {
      return DisplayStatus.pending_approval;
    }
    if (version.dual_approval_state === "rejected_approval") {
      return DisplayStatus.rejected;
    }
    if (version.id === activeVersionId) {
      return DisplayStatus.active;
    }
    return DisplayStatus.archived;
  };

  const displayStatus = getDisplayStatus();
  const isPendingApproval = displayStatus === DisplayStatus.pending_approval;
  const isRejected = displayStatus === DisplayStatus.rejected;
  const isArchived = displayStatus === DisplayStatus.archived;
  const isApproved = version.dual_approval_state === "approved";
  const dotColorClass = getStatusDotClass(displayStatus);
  const shouldFetchApprovalDetails =
    isExpanded &&
    Boolean(version.dual_approval_id) &&
    responderName === null &&
    rejectionReason === null;

  useEffect(() => {
    setResponderName(null);
    setRejectionReason(null);
  }, [version.dual_approval_id]);

  useEffect(() => {
    if (!shouldFetchApprovalDetails || !version.dual_approval_id) {
      return;
    }

    fetchDualApproval(version.dual_approval_id).then((approval) => {
      if (approval?.responder) {
        const fullName =
          `${approval.responder.first_name} ${approval.responder.last_name}`.trim();
        setResponderName(fullName || approval.responder.username || "");
      } else {
        setResponderName("");
      }
      setRejectionReason(approval?.note || "");
    });
  }, [fetchDualApproval, shouldFetchApprovalDetails, version.dual_approval_id]);

  const formatted = formatBulkTemplateVersion(version);
  const detailFields: DetailField[] = [];
  if (formatted.userFullName) {
    detailFields.push({
      label: l10n.getString("label-created-by"),
      value: formatted.userFullName,
      tooltipText: formatted.userEmail ?? undefined,
    });
  }
  if (responderName && isRejected) {
    detailFields.push({
      label: l10n.getString("label-rejected-by"),
      value: responderName,
    });
  }
  if (responderName && isApproved) {
    detailFields.push({
      label: l10n.getString("label-approved-by"),
      value: responderName,
    });
  }
  if (rejectionReason && isRejected) {
    detailFields.push({
      label: l10n.getString("template-reject-reason-label"),
      value: rejectionReason,
    });
  }

  if (formatted.userChangeLog) {
    detailFields.push({
      label: l10n.getString("change-log-label", null, "Change log"),
      value: formatted.userChangeLog,
    });
  }

  return (
    <CollapsibleCard
      isOpen={isExpanded}
      onOpen={() => setIsExpanded(true)}
      onClose={() => setIsExpanded(false)}
      renderTitle={(isOpen: boolean) => (
        <Row alignItems="center">
          <Row.Item>
            <div className={`${styles.versionCardTitle} padding--all--l`}>
              <span className={dotColorClass}>
                <span className={styles.versionDot} />
              </span>
              <div>
                <Row alignItems="center">
                  <Row.Item shrink>
                    <span className="fontWeight--bold fontSize--m">
                      {`${l10n.getString("label-version")} ${
                        version.version_number
                      }`}
                    </span>
                  </Row.Item>
                  {isOpen && (
                    <Row.Item shrink>
                      <span className={styles.versionStatusTag}>
                        <StatusTag status={displayStatus} hideIcon />
                      </span>
                    </Row.Item>
                  )}
                </Row>
                <div className="fontSize--s fontColor--secondary">
                  {Filters.shortMonthDayYear(version.created_at)}
                </div>
              </div>
            </div>
          </Row.Item>
          <Row.Item shrink>
            <span
              className={`narmi-icon-chevron-${
                isOpen ? "up" : "down"
              } padding--right--l`}
            />
          </Row.Item>
        </Row>
      )}
    >
      <div>
        {detailFields.map(({ label, value, tooltipText }, i) => (
          <div key={i} className="margin--bottom--l">
            <span className="fontSize--s fontColor--secondary">{label}</span>
            <div
              className={`fontSize--m fontColor--primary ${styles.changeLogContainer}`}
            >
              {tooltipText ? (
                <Tooltip text={tooltipText}>{value}</Tooltip>
              ) : (
                value
              )}
            </div>
          </div>
        ))}

        <Row alignItems="center">
          <Row.Item>
            {isPendingApproval && formatted.dualApprovalId ? (
              <Button
                kind="plain"
                size="s"
                onClick={() => {
                  navigate(
                    `/approvals/templates?dualApprovalId=${formatted.dualApprovalId}`
                  );
                }}
              >
                {l10n.getString("label-view-approvals")}
                <span className="narmi-icon-chevron-right" />
              </Button>
            ) : (
              <Button
                kind="plain"
                size="s"
                onClick={() => onViewPayments(version)}
              >
                {l10n.getString("label-view")}
                <span className="narmi-icon-chevron-right" />
              </Button>
            )}
          </Row.Item>
          {(isRejected || isArchived) &&
            groupState !== "archived" &&
            onCopyToNewVersion && (
              <Row.Item shrink>
                <Button
                  kind="tonal"
                  size="xs"
                  startIcon="copy"
                  onClick={() => onCopyToNewVersion(version.id)}
                >
                  {l10n.getString("label-copy-to-new-version")}
                </Button>
              </Row.Item>
            )}
        </Row>
      </div>
    </CollapsibleCard>
  );
};

export default VersionCard;
